import React, { useEffect, useRef } from "react";
import { Modal } from "antd";
import { useNavigate } from "react-router-dom";


const onboardingSteps = [
  {
    title: "Step 1: Add Your Website",
    desc: "Add your domain to start tracking visitors.",
    cta: "Add",
    navigateTo:"/script",
    successMessage: "\u2713 Domain added successfully",
  },
  {
    title: "Step 2: Install & Verify The Tracking Script",
    desc: "Install the Kwin pixel on your website. Verify that the tracking script is working correctly.",
    cta: "View Guide",
    navigateTo:"/pixel-details",
    successMessage: "\u2713 Script installed & verified",
  },
  {
    title: "Step 3: Choose Pages to Track",
    desc: "Select pages where you want visitor identification.",
    cta: "Add Pages",
    navigateTo:"/script",
    successMessage: "\u2713 Pages added",
  },
  {
    title: "Step 4: Define Business & Buyer Persona",
    desc: "Tell us about your business and ideal customers.",
    cta: "Setup",
    navigateTo:"/knowledge",
    successMessage: "\u2713 Business info saved",
  },
  {
    title: "Step 5: Add Email Accounts",
    desc: "Connect your email to start automated outreach.",
    cta: "Connect email",
    navigateTo:"/email-outreach",
    successMessage: "\u2713 Email connected",
  },
  {
    title: "Step 6: Integrate Sales Tools",
    desc: "Push leads to Slack, CRM, and other tools.",
    cta: "Connect",
    navigateTo:"/integration-sales",
    successMessage: "\u2713 Integration completed",
  },
];

const StepCard = ({ title, desc, status, cta, locked, isActive, successMessage, innerRef,navigateTo, setIsOpen }) => {
  const statusStyles = {
    true: "bg-green-500 text-white",
    false: "bg-gray-200 text-gray-400"
  };

  const navigate = useNavigate();

  const checkIcon = status ? "\u2713" :  "\u23F3";

  return (
    <div
      ref={innerRef}
      className={`bg-white p-6 rounded-2xl shadow-sm flex justify-between items-center ${
        isActive ? "border-2 border-purple-500" : ""
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Check Circle */}
        <div
          className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${statusStyles[status]}`}
        >
          {checkIcon}
        </div>

        <div>
          <h3 className="font-semibold text-lg">
            {title} 
            {locked && <span className="ml-2 text-sm text-gray-400">
              {/* 🔒 unicode for locked */}
              &#128274;
              </span>}
          </h3>
          <p className="text-sm text-gray-600 mt-1">{desc}</p>

          {/* Micro Success Feedback */}
          {status && successMessage && (
            <p className="text-sm text-green-600 mt-2">
              {successMessage}
            </p>
          )}
        </div>
      </div>

      <div className="text-right">
        <button
          className={`px-4 py-2 rounded-lg text-sm ${
            (status || locked) ? "bg-gray-200 text-gray-400" : "bg-purple-600 text-white"
          }`}
          onClick={()=>{navigate(navigateTo); setIsOpen(false);}}
          disabled={status || locked}
        >
          {cta}
        </button>
      </div>
    </div>
  );
};

export default function KwinOnboardingModal({ isOpen, setIsOpen, kwinOnboardingStatus, setKwinOnboardingStatus }) {
  const stepRefs = useRef([]);

  const completedCount = kwinOnboardingStatus?.filter(Boolean).length || 0;
  const falseIndex = kwinOnboardingStatus?.findIndex(s => !s);
  const firstIncompleteIndex = falseIndex === -1 ? (kwinOnboardingStatus?.length || 0) : falseIndex;
  const totalSteps = onboardingSteps.length;
  const progressPercentage = Math.round((completedCount / totalSteps) * 100) || 0;

  useEffect(() => {
    if (firstIncompleteIndex < totalSteps && stepRefs.current[firstIncompleteIndex]) {
      stepRefs.current[firstIncompleteIndex].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [firstIncompleteIndex, totalSteps]);

  return (
    <Modal
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      footer={null}
      width={800}
      centered
      styles={{
        content: {
          padding: 0,
          backgroundColor: '#f1f5f9',
          overflow: 'hidden'
        },
        body: { 
          padding: '24px', 
          maxHeight: '95vh', 
          overflowY: 'auto' 
        }
      }}
    >
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div className="bg-white p-6 rounded-2xl shadow-sm">
          <h1 className="text-2xl font-semibold">Get Started with Kwin</h1>
          <p className="text-gray-600 mt-2">
            Complete these steps to start identifying and reaching your website visitors.
          </p>
        </div>

        {/* Progress */}
        <div className="bg-white p-6 rounded-2xl shadow-sm">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">Progress</p>
            <p className="text-sm font-medium">{completedCount} / {totalSteps} completed</p>
          </div>
          <div className="w-full bg-gray-200 h-2 rounded-full mt-3 overflow-hidden">
            <div className="bg-purple-600 h-full rounded-full transition-all duration-500 ease-in-out" style={{ width: `${progressPercentage}%` }}></div>
          </div>
        </div>

        {/* Steps */}
        {onboardingSteps.map((step, index) => {

          const isStepCompleted = kwinOnboardingStatus ? kwinOnboardingStatus[index] : false;
          // lock completed steps and steps if the first step is incomplete then second will be locked and so on until 3rd step
          let isLocked = false;
          if((index == 1 || index == 2) && !kwinOnboardingStatus[index - 1]){
            isLocked = true;
          }

          return (
            <StepCard
              setIsOpen={setIsOpen}
              key={index}
              title={step.title}
              desc={step.desc}
              status={isStepCompleted}
              cta={step.cta}
              locked={isLocked}
              successMessage={step.successMessage}
              isActive={firstIncompleteIndex === index}
              innerRef={el => (stepRefs.current[index] = el)}
              navigateTo={step.navigateTo}
            />
          );
        })}

      </div>
    </Modal>
  );
}

